import React, { useEffect, useState } from 'react';
import BrandLayout from '@/components/BrandLayout';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Search, AlertCircle } from 'lucide-react';

export default function BrandProducts() {
    const [products, setProducts] = useState([]);
    const [q, setQ] = useState('');
    const [status, setStatus] = useState('');
    const [editing, setEditing] = useState({}); // {productId: stockValue}

    const load = () => {
        const params = new URLSearchParams();
        if (q) params.append('q', q);
        if (status) params.append('status', status);
        api.get(`/brand/products?${params.toString()}`).then(({ data }) => setProducts(data)).catch(() => {});
    };
    useEffect(load, [status]);

    const updateStock = async (pid) => {
        const newStock = parseInt(editing[pid]);
        if (isNaN(newStock) || newStock < 0) return toast.error('Invalid stock value');
        try {
            await api.post(`/brand/products/${pid}/stock`, { stock: newStock });
            toast.success('Stock updated');
            setEditing((e) => { const c = { ...e }; delete c[pid]; return c; });
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Failed');
        }
    };

    return (
        <BrandLayout>
            <div className="p-10" data-testid="brand-products-page">
                <div className="mb-8 flex flex-wrap items-end justify-between gap-6">
                    <div>
                        <div className="overline">Catalog</div>
                        <h1 className="font-serif text-4xl text-inorbit-blue mt-2">Products</h1>
                        <div className="text-sm text-text-secondary mt-1">{products.length} SKU{products.length !== 1 ? 's' : ''}</div>
                    </div>
                    <div className="flex gap-3 flex-wrap items-center">
                        <div className="relative">
                            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary" />
                            <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && load()} placeholder="Search by name" className="input-box pl-9 w-64" data-testid="product-search" />
                        </div>
                        <select value={status} onChange={(e) => setStatus(e.target.value)} className="input-box" data-testid="product-status-filter">
                            <option value="">All</option>
                            <option value="active">Active</option>
                            <option value="low">Low Stock (≤10)</option>
                            <option value="out">Out of Stock</option>
                        </select>
                    </div>
                </div>

                <div className="bg-white border border-border-subtle rounded-md overflow-hidden">
                    {products.length === 0 ? (
                        <div className="p-16 text-center text-text-secondary">No products found. Upload via Inventory page.</div>
                    ) : (
                        <table className="w-full text-sm" data-testid="products-table">
                            <thead className="bg-cream text-text-secondary text-[11px] uppercase tracking-widest">
                                <tr>
                                    <th className="text-left p-4">Product</th>
                                    <th className="text-left p-4">SKU</th>
                                    <th className="text-left p-4">Category</th>
                                    <th className="text-right p-4">Price</th>
                                    <th className="text-center p-4">Stock</th>
                                    <th className="p-4"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {products.map((p) => (
                                    <tr key={p.id} className={`border-t border-border-subtle ${p.stock === 0 ? 'bg-rose-50/30' : ''}`} data-testid={`product-row-${p.id}`}>
                                        <td className="p-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 bg-cream overflow-hidden rounded shrink-0">
                                                    {p.images?.[0] && <img src={p.images[0]} alt="" className="w-full h-full object-cover" />}
                                                </div>
                                                <div className="text-inorbit-blue truncate max-w-xs">{p.name}</div>
                                            </div>
                                        </td>
                                        <td className="p-4 font-mono text-xs text-text-tertiary">{p.sku}</td>
                                        <td className="p-4 text-text-secondary capitalize">{p.category}</td>
                                        <td className="p-4 text-right text-inorbit-blue font-semibold">₹{Number(p.price).toLocaleString('en-IN')}</td>
                                        <td className="p-4 text-center">
                                            {editing[p.id] !== undefined ? (
                                                <div className="flex items-center gap-1 justify-center">
                                                    <input
                                                        type="number"
                                                        value={editing[p.id]}
                                                        onChange={(e) => setEditing((s) => ({ ...s, [p.id]: e.target.value }))}
                                                        className="input-box w-20 text-center py-1"
                                                        autoFocus
                                                        data-testid={`stock-input-${p.id}`}
                                                    />
                                                    <button onClick={() => updateStock(p.id)} className="text-[11px] text-inorbit-orange font-semibold ml-1" data-testid={`stock-save-${p.id}`}>Save</button>
                                                </div>
                                            ) : (
                                                <button onClick={() => setEditing((s) => ({ ...s, [p.id]: p.stock }))} className={`inline-flex items-center gap-1 hover:underline ${p.stock === 0 ? 'text-rose-600' : p.stock <= 10 ? 'text-amber-700' : 'text-inorbit-blue'}`} data-testid={`stock-edit-${p.id}`}>
                                                    {p.stock === 0 && <AlertCircle size={12} />}
                                                    {p.stock}
                                                </button>
                                            )}
                                        </td>
                                        <td className="p-4 text-right">
                                            <span className={`pill ${p.is_active ? 'pill-blue' : 'pill-outline'} text-[10px]`}>{p.is_active ? 'Active' : 'Inactive'}</span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </BrandLayout>
    );
}
